import { useState, useEffect } from 'react';
import clsx from 'clsx';
import { LanguageSwitcher, Language } from '../language-switcher/LanguageSwitcher';

import logoLight from '../../../../assets/assets-home/svg/logo-light.svg';
import logoDark from '../../../../assets/assets-home/svg/logo-dark.svg';

export interface NavLink {
  label: string;
  href: string;
  onClick?: () => void;
}

export interface PublicHeaderProps {
  variant: 'light' | 'dark';
  navLinks: NavLink[];
  showLanguageSwitcher?: boolean;
  currentLanguage?: Language;
  onLanguageChange?: (language: Language) => void;
  position?: 'static' | 'absolute';
  centerLogoOnMobile?: boolean;
  borderBottom?: boolean;
  className?: string;
}

export function PublicHeader({
  variant,
  navLinks,
  showLanguageSwitcher = true,
  currentLanguage,
  onLanguageChange,
  position = 'static',
  centerLogoOnMobile = false,
  borderBottom = false, 
  className,
}: PublicHeaderProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        setIsMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isMenuOpen) {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    } else {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, [isMenuOpen]);

  const logo = variant === 'light' ? logoLight : logoDark;

  const headerClasses = clsx(
    'px-5 sm:px-8 lg:px-10 py-2.5 sm:py-[18px] lg:py-[34.82px] w-full z-[11]',
    {
      'absolute top-0 left-0 right-0': position === 'absolute',
    },
    className
  );

  const headerStyle = variant === 'dark' ? { boxShadow: '0 0.5px 0 0 #EFE8DF' } : { boxShadow: '0 0.5px 0 0 #4A4A4A4D' };

  const navLinkClasses = clsx(
    'text-xl uppercase transition-colors select-none [-webkit-tap-highlight-color:transparent]',
    {
      'text-black hover:text-[#9B2626] active:text-black': variant === 'light',
      'text-white hover:text-[#EFE8DF] active:text-white': variant === 'dark',
    }
  );

  const burgerLineColor = variant === 'light' ? 'text-black' : 'text-white';

  const handleNavClick = (link: NavLink) => {
    setIsMenuOpen(false);
    link.onClick?.();
  };

  return (
    <>
      <header className={headerClasses} style={window.innerWidth > 640 || borderBottom ? headerStyle : {}}>
        <div className="max-w-[1520px] flex justify-between items-center w-full mx-auto">
          {centerLogoOnMobile && <div className="w-[25px] lg:hidden" aria-hidden="true" />}

          <div className={clsx({ 'flex-1 flex justify-center lg:flex-none': centerLogoOnMobile })}>
            <a href="/">
              <img src={logo} alt="Xplorius Logo" className="h-auto lg:max-w-full sm:max-w-[109px] max-w-[81px]" />
            </a>
          </div>

          <ul className="hidden lg:flex items-center gap-[60px]">
            {navLinks.map((link, index) => (
              <li key={index}>
                <a
                  href={link.href}
                  className={navLinkClasses}
                  onClick={() => link.onClick?.()}
                >
                  {link.label}
                </a>
              </li>
            ))}
          </ul>

          {showLanguageSwitcher && (
            <div className="hidden lg:block">
              <LanguageSwitcher
                variant={variant}
                currentLanguage={currentLanguage}
                onLanguageChange={onLanguageChange}
              />
            </div>
          )}

          <button
            onClick={() => setIsMenuOpen(true)}
            aria-label="Open menu"
            className='lg:hidden'
          >
            <svg className={`sm:block hidden ${burgerLineColor}`} width="25" height="13" viewBox="0 0 25 13" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="25" height="1" fill="currentColor" />
              <rect y="12" width="25" height="1" fill="currentColor" />
              <rect x="11" y="6" width="14" height="1" fill="currentColor" />
            </svg>

            <svg className={`sm:hidden ${burgerLineColor}`} width="13" height="7" viewBox="0 0 13 7" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="12.5" height="0.5" fill="currentColor" />
              <rect y="6" width="12.5" height="0.5" fill="currentColor" />
              <rect x="5.5" y="3" width="7" height="0.5" fill="currentColor" />
            </svg>

          </button>
        </div>
      </header>

      <div
        className={clsx(
          'fixed inset-0 z-[100] lg:hidden transition-opacity duration-300 max-h-[100dvh]',
          {
            'opacity-100 visible': isMenuOpen,
            'opacity-0 invisible pointer-events-none': !isMenuOpen,
          }
        )}
      >
        <div
          className="absolute inset-0 bg-[#00000066] transition-opacity duration-300"
          style={{ opacity: isMenuOpen ? 1 : 0 }}
          onClick={() => setIsMenuOpen(false)}
        />

        {/* Right sidebar panel */}
        <div
          className={clsx(
            'absolute top-0 right-0 h-full bg-white rounded-l-[12px] p-4 flex flex-col w-[220px] transition-transform duration-300 ease-out',
            {
              'translate-x-0': isMenuOpen,
              'translate-x-full': !isMenuOpen,
            }
          )}
        >
          <a href="/" onClick={() => setIsMenuOpen(false)}>
            <img src={logoLight} alt="Xplorius Logo" className="h-auto max-w-[82px] mb-8" />
          </a>

          <nav>
            <ul className="flex flex-col gap-3">
              {navLinks.map((link, index) => (
                <li key={index}>
                  <a
                    href={link.href}
                    className="block py-[1px] text-xs font-medium text-[#4A4A4A] uppercase select-none [-webkit-tap-highlight-color:transparent]"
                    onClick={() => handleNavClick(link)}
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>

            {showLanguageSwitcher && (
              <div className="mt-4 w-full">
                <LanguageSwitcher
                  variant="light"
                  currentLanguage={currentLanguage}
                  onLanguageChange={onLanguageChange}
                />
              </div>
            )}
          </nav>
        </div>
      </div>
    </>
  );
}
